import { BookmarkToggle } from '@/app/features/bookmarks/BookmarkToggle.tsx';
import { buildBookmarkDescription } from '@/app/features/bookmarks/types.ts';
import { useProjectPermissions } from '@/app/features/tribes-projects/projects/useProjectPermissions.ts';
import { useProject } from '@/app/features/tribes-projects/projects/useProjects.ts';
import { useTribeWithPositions } from '@/app/features/tribes-projects/tribes/useTribesWithPositions.ts';
import { useDocumentTitle } from '@/app/platform/core/browser/useDocumentTitle.ts';
import { AppLayout } from '@/app/platform/core/layout/AppLayout.tsx';
import { ThemedConfirmDialog } from '@/app/platform/core/layout/themes/components/ThemedConfirmDialog.tsx';
import { ThemedLoadingSpinner } from '@/app/platform/core/layout/themes/components/ThemedLoadingSpinner.tsx';
import { ThemedSection } from '@/app/platform/core/layout/themes/components/ThemedSection.tsx';
import { ThemeProvider } from '@/app/platform/core/layout/themes/ThemeContext.tsx';
import { errorStyle } from '@/app/platform/core/layout/themes/theme.styles.tsx';

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { guitarSongsService } from './service.ts';
import { SongBlockClipboardPreviewModal } from './SongBlockClipboardPreviewModal.tsx';
import { SongDetailBody } from './SongDetailBody.tsx';
import { SongDifficultyBand } from './SongDifficultyBand.tsx';
import { SongLabelsBand } from './SongLabelsBand.tsx';
import { SongLearningToolsPanel } from './SongLearningToolsPanel.tsx';
import { SongMasteryBand } from './SongMasteryBand.tsx';
import { songDocumentTitle } from './songDocumentTitle.ts';
import { GuitarSongContentType, GuitarSongState } from './types.ts';
import { useGuitarSong } from './useGuitarSong.ts';
import { useGuitarSongLabels } from './useGuitarSongLabels.ts';
import { usePresentationPageSize } from './usePresentationPageSize.ts';
import { useSongBlockClipboard } from './useSongBlockClipboard.ts';
import { useSongListPath } from './useSongListPath.ts';

const SongDetailPageContent: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { tribeId, projectId, songId } = useParams<{ tribeId: string; projectId: string; songId: string }>();

  const { tribe } = useTribeWithPositions(tribeId || null);
  const { project } = useProject(projectId || null);
  const { isManager, canEdit } = useProjectPermissions(tribeId || null, projectId || null);
  const hook = useGuitarSong(songId || null);
  const { song, loading, error } = hook;
  const labelsHook = useGuitarSongLabels(projectId || null);
  const clipboardHook = useSongBlockClipboard();
  const { maxWidth } = usePresentationPageSize();
  useDocumentTitle(song ? songDocumentTitle(song, t('common.edit')) : undefined);

  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [clipboardPreviewOpen, setClipboardPreviewOpen] = useState(false);
  const [labelsModalOpen, setLabelsModalOpen] = useState(false);
  const [learningToolsOpen, setLearningToolsOpen] = useState(false);
  const presentPath = `/app/tribes/${tribeId}/projects/${projectId}/songs/${songId}/present`;

  // A completed song only offers the presentation screen -- redirect away from the edit URL
  // if it's reached directly (e.g. a bookmark or a stale link).
  useEffect(() => {
    if (song?.song_state === GuitarSongState.completed) navigate(presentPath, { replace: true });
  }, [song?.song_state, presentPath, navigate]);

  const breadcrumbs = useMemo(
    () => [
      { label: t('common.home'), path: '/app' },
      { label: t('tribes.title'), path: '/app/tribes' },
      { label: tribe?.name || t('common.loading'), path: `/app/tribes/${tribeId}` },
      { label: project?.name || t('common.loading'), path: `/app/tribes/${tribeId}/projects/${projectId}` },
      { label: song ? songDocumentTitle(song, t('common.edit')) : t('common.loading') },
    ],
    [tribe?.name, project?.name, song, tribeId, projectId, t],
  );

  const bookmarkSlot = song?.title ? (
    <BookmarkToggle
      pagePath={location.pathname}
      pageTitle={song.title}
      pageDescription={buildBookmarkDescription(breadcrumbs)}
    />
  ) : null;

  const songListPath = useSongListPath(tribeId || null, projectId || null);

  const handleArchive = async () => {
    if (!song) return;
    setArchiving(true);
    try {
      await guitarSongsService.archiveSong(song.id);
      navigate(songListPath);
    } finally {
      setArchiving(false);
      setArchiveConfirmOpen(false);
    }
  };

  const handleMarkCompleted = async () => {
    await hook.updateSongFields({ song_state: GuitarSongState.completed });
    navigate(presentPath);
  };

  const menuActions = useMemo(() => {
    if (!song) return [];
    return [
      {
        id: 'guitar.song.backToList',
        icon: 'search' as const,
        label: t('guitarSong.detail.backToList'),
        path: songListPath,
      },
      {
        id: 'guitar.song.presentationMode',
        icon: 'eye' as const,
        label: t('guitarSong.layout.presentationMode'),
        path: presentPath,
      },
      {
        id: 'guitar.song.learningTools',
        icon: 'headphones' as const,
        label: t('guitarSong.learningTools.title'),
        onClick: () => setLearningToolsOpen(true),
      },
      ...(canEdit
        ? [{
            id: 'guitar.song.markCompleted',
            icon: 'check-circle' as const, label: t('guitarSong.detail.markCompleted'),
            onClick: handleMarkCompleted,
          }]
        : []),
      ...(canEdit
        ? [{
            id: 'guitar.song.manageLabels',
            icon: 'tag' as const, label: t('guitarSong.labels.manageLabels'),
            onClick: () => setLabelsModalOpen(true),
          }]
        : []),
      ...(canEdit && clipboardHook.clipboard && song.content_type !== GuitarSongContentType.pdf
        ? [{
            id: 'guitar.song.previewClipboard',
            icon: 'clipboard' as const, label: t('guitarSong.layout.previewClipboard'),
            onClick: () => setClipboardPreviewOpen(true),
          }]
        : []),
      ...(isManager
        ? [{ id: 'guitar.song.archive', icon: 'trash' as const, label: t('guitarSong.detail.archive'), onClick: () => setArchiveConfirmOpen(true) }]
        : []),
    ];
  }, [song, isManager, canEdit, clipboardHook.clipboard, t, presentPath, songListPath]);

  if (loading && !song) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <ThemedLoadingSpinner size="sm" />
        </div>
      </AppLayout>
    );
  }
  if (error || !song) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div style={errorStyle}>
          <strong>{t('common.error')}</strong> {error || t('guitarSong.detail.loadError')}
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs} menuActions={menuActions} bookmarkSlot={bookmarkSlot}>
      <ThemedSection themeId="main_1">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <SongLabelsBand
            song={song} hook={hook} labelsHook={labelsHook} canManage={canEdit && isManager}
            isManageOpen={labelsModalOpen} onCloseManage={() => setLabelsModalOpen(false)}
          />
          <SongDifficultyBand song={song} hook={hook} canEdit={canEdit} />
          <SongMasteryBand song={song} hook={hook} />
        </div>
        <div style={{ maxWidth, margin: maxWidth ? '0 auto' : undefined }}>
          <SongDetailBody
            song={song} canEdit={canEdit} isManager={isManager} hook={hook} labelsHook={labelsHook}
            clipboardHook={clipboardHook}
          />
        </div>
      </ThemedSection>
      <ThemedConfirmDialog
        isOpen={archiveConfirmOpen}
        onClose={() => setArchiveConfirmOpen(false)}
        onConfirm={handleArchive}
        title={t('guitarSong.detail.archiveTitle')}
        message={t('guitarSong.detail.archiveMessage', { title: song.title })}
        variant="danger"
        isLoading={archiving}
      />
      <SongBlockClipboardPreviewModal
        isOpen={clipboardPreviewOpen} onClose={() => setClipboardPreviewOpen(false)}
        copied={clipboardHook.clipboard} song={song}
      />
      <SongLearningToolsPanel
        song={song} hook={hook} canManage={canEdit && isManager}
        isOpen={learningToolsOpen} onClose={() => setLearningToolsOpen(false)}
      />
    </AppLayout>
  );
};

export const SongDetailPage: React.FC = () => (
  <ThemeProvider defaultTheme="default">
    <SongDetailPageContent />
  </ThemeProvider>
);
